/**
 * PreviewRail Component
 *
 * Displays a collection rail preview with:
 * - Center card showing real submission data
 * - Skeleton cards for context (2 on each side)
 * - Heavy gradient shadows from left/right to focus attention
 * - Hover effects enabled on center card only
 */

import type { ReactElement } from "react";
import { PreviewHorizontalCard } from "./PreviewHorizontalCard";
import { PreviewSkeletonHorizontalCard } from "./PreviewSkeletonHorizontalCard";
import { PreviewSkeletonVerticalCard } from "./PreviewSkeletonVerticalCard";
import { PreviewVerticalCard } from "./PreviewVerticalCard";
import type { TitlePreviewData } from "./TitlePreview";

interface PreviewRailProps {
  readonly title: string;
  readonly description: string;
  readonly cardType: "HORIZONTAL" | "VERTICAL";
  readonly previewData: TitlePreviewData;
  readonly submissionId?: string | null;
  readonly titleId?: string | null;
}

export function PreviewRail({
  title,
  description,
  cardType,
  previewData,
  submissionId,
  titleId,
}: PreviewRailProps): ReactElement {
  return (
    <div className="space-y-2">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <p className="text-sm text-neutral-400 pb-4">{description}</p>
      </div>

      {/* Rail container with gradient shadows */}
      <div className="relative overflow-hidden bg-transparent">
        {/* Left gradient shadow - fades to page background */}
        <div className="absolute left-0 top-0 bottom-0 w-80 bg-gradient-to-r from-[#070707] via-[#070707]/80 to-transparent z-10 pointer-events-none" />

        {/* Rail items */}
        <div className="flex gap-6 overflow-x-hidden py-4 px-8 justify-center">
          {/* Left skeleton cards */}
          {cardType === "HORIZONTAL" ? (
            <>
              <PreviewSkeletonHorizontalCard />
              <PreviewSkeletonHorizontalCard />
            </>
          ) : (
            <>
              <PreviewSkeletonVerticalCard />
              <PreviewSkeletonVerticalCard />
            </>
          )}

          {/* Center: Real preview card */}
          {cardType === "HORIZONTAL" ? (
            <PreviewHorizontalCard data={previewData} submissionId={submissionId} titleId={titleId} />
          ) : (
            <PreviewVerticalCard data={previewData} submissionId={submissionId} titleId={titleId} />
          )}

          {/* Right skeleton cards */}
          {cardType === "HORIZONTAL" ? (
            <>
              <PreviewSkeletonHorizontalCard />
              <PreviewSkeletonHorizontalCard />
            </>
          ) : (
            <>
              <PreviewSkeletonVerticalCard />
              <PreviewSkeletonVerticalCard />
            </>
          )}
        </div>

        {/* Right gradient shadow - fades to page background */}
        <div className="absolute right-0 top-0 bottom-0 w-80 bg-gradient-to-l from-[#070707] via-[#070707]/80 to-transparent z-10 pointer-events-none" />
      </div>
    </div>
  );
}
